import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import {
  Home,
  Calendar,
  ClipboardList,
  FileText,
  Users,
  Settings,
  Shield,
  Building,
  User,
  MessageSquare,
  BookOpen,
  Search,
  Clock,
  AlertTriangle,
  Bug,
  DollarSign,
  Target
} from 'lucide-react';

export default function BottomNav({ userRole }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    const updateModalState = () => {
      setIsModalOpen(document.body.classList.contains('modal-open'));
    };

    updateModalState();

    const observer = new MutationObserver(updateModalState);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const roleMenuItems = {
    super_admin: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Opgaver', url: 'Assignments', icon: ClipboardList },
      { name: 'Vagtplan', url: 'ShiftPlanning', icon: Calendar },
      { name: 'Økonomi', url: 'Okonomi', icon: DollarSign },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    system_admin: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Opgaver', url: 'Assignments', icon: ClipboardList },
      { name: 'Vagtplan', url: 'ShiftPlanning', icon: Calendar },
      { name: 'Brugere', url: 'AdminTools', icon: Users },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    admin: [
      { name: 'Hjem', url: 'AdminDashboard', icon: Home },
      { name: 'Brugere', url: 'Admin', icon: Users },
      { name: 'Sager', url: 'AdminCases', icon: FileText },
      { name: 'Søge', url: '/admin/søge', icon: Search },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    coordinator: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Planlægning', url: 'Planning', icon: Calendar },
      { name: 'Vagtplan', url: 'ShiftPlanning', icon: Calendar },
      { name: 'Opgaver', url: 'Assignments', icon: ClipboardList },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    social_worker: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Sager', url: 'SocialCases', icon: FileText },
      { name: 'Vagtplan', url: 'ShiftPlanning', icon: Calendar },
      { name: 'Journal', url: 'Journal', icon: BookOpen },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    socialraadgiver: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Sager', url: 'SocialCases', icon: FileText },
      { name: 'Handleplaner', url: 'Handleplaner', icon: Target },
      { name: 'Journal', url: 'Journal', icon: BookOpen },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    medarbejder: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Vagter', url: 'ShiftPlanning', icon: Calendar },
      { name: 'Sager', url: 'SocialCases', icon: FileText },
      { name: 'Journal', url: 'Journal', icon: BookOpen },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    vikar: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Find Vagter', url: 'FindVagter', icon: Search },
      { name: 'Min Kalender', url: 'MineVagter', icon: Calendar },
      { name: 'Timesedler', url: 'MyTimesheets', icon: Clock },
      { name: 'Profil', url: 'VikarProfile', icon: User }
    ],
    recruiter: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Kandidater', url: 'Candidates', icon: Users },
      { name: 'Medarbejdere', url: 'Medarbejdere', icon: ClipboardList },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    kommune: [
      { name: 'Hjem', url: '/dashboard/kommune', icon: Home },
      { name: 'Timer', url: '/kommune/godkend-timer', icon: Clock },
      { name: 'Vagter', url: '/admin/shifts/create', icon: Calendar },
      { name: 'Vikarer', url: '/admin/vikare', icon: Users },
      { name: 'Profil', url: '/profile', icon: User }
    ],
    kommunemedarbejder: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Anmod', url: 'RequestStaff', icon: ClipboardList },
      { name: 'Anmodninger', url: 'MyRequests', icon: FileText },
      { name: 'Besked', url: 'Communication', icon: MessageSquare },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    client: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Sager', url: 'SocialCases', icon: FileText },
      { name: 'Anmod', url: 'RequestStaff', icon: ClipboardList },
      { name: 'Besked', url: 'Communication', icon: MessageSquare },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ],
    journal_viewer: [
      { name: 'Hjem', url: 'Dashboard', icon: Home },
      { name: 'Journal', url: 'Journal', icon: BookOpen },
      { name: 'Sager', url: 'SocialCases', icon: FileText },
      { name: 'Søg', url: 'Search', icon: Search },
      { name: 'Profil', url: 'MyProfile', icon: User }
    ]
  };

  const navItems = roleMenuItems[userRole] || roleMenuItems.vikar;

  return (
    <nav 
      className={`fixed bottom-0 left-0 right-0 lg:hidden pointer-events-none ${isModalOpen ? 'hidden' : ''}`}
      data-tour="bottom-nav"
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)',
        zIndex: 9999,
      }}
    >
      {/* Floating capsule container - centered on sm+ screens */}
      <div 
        className="bg-white/90 dark:bg-gray-900/90 text-app-text dark:text-white backdrop-blur-lg border border-app-border/40 dark:border-white/10 rounded-full px-6 sm:px-8 py-2 shadow-2xl pointer-events-auto mx-auto w-[calc(100%-2rem)] sm:w-fit sm:min-w-[320px] absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 transition-colors duration-200"
        style={{
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          paddingLeft: 'max(env(safe-area-inset-left, 0px), 1.5rem)',
          paddingRight: 'max(env(safe-area-inset-right, 0px), 1.5rem)',
          pointerEvents: 'auto',
          zIndex: 9999,
        }}
      >
        <div className="flex items-center justify-between sm:justify-center sm:gap-6 pointer-events-auto" style={{ pointerEvents: 'auto', zIndex: 9999 }}>
          {navItems.map((item) => {
            const url = item.url.startsWith('/') ? item.url : createPageUrl(item.url);
            const isActive = location.pathname === url;
            const Icon = item.icon;

            const handleNavClick = (e) => {
              e.stopPropagation();
              console.log('BottomNav button clicked:', item.name, url);
              navigate(url);
            };

            return (
              <button
                key={item.name}
                type="button"
                onClick={handleNavClick}
                className={`
                  flex flex-col items-center justify-center gap-0.5 transition-all duration-200
                  rounded-full px-2 sm:px-3 py-2 min-w-[56px] touch-manipulation
                  pointer-events-auto relative z-10 cursor-pointer border-0 bg-transparent
                  ${isActive 
                    ? 'text-app-primary bg-app-primary/15 scale-105' 
                    : 'text-app-text-secondary hover:text-app-text hover:bg-app-secondary/50 active:scale-95'
                  }
                `}
                aria-label={item.name}
                style={{ 
                  pointerEvents: 'auto', 
                  zIndex: 10000, 
                  position: 'relative',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <Icon className="w-5 h-5 flex-shrink-0 pointer-events-none" />
                <span className="text-[10px] font-medium leading-tight text-center whitespace-nowrap pointer-events-none">
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}